<script setup>
import { ref, computed, onMounted } from "vue";
import { useAuth0 } from "@auth0/auth0-vue";

// Single Auth0 destructuring call
const { getAccessTokenSilently, logout, user, isAuthenticated } = useAuth0();

const handleLogout = () => {
  logout({ logoutParams: { returnTo: window.location.origin } });
};

const isAdmin = computed(() => {
  const roles = user.value?.['https://blything.co.uk/roles'] || [];
  return roles.includes('Admin');
});

// User Management State
const users = ref([]);
const loading = ref(false);
const error = ref(null);
const searchQuery = ref("");

// Fetch users from serverless API
const fetchUsers = async () => {
  if (!isAdmin.value) return;

  loading.value = true;
  error.value = null;

  try {
    const token = await getAccessTokenSilently();
    
    // Dynamically select base URL (VUE_APP_API_BASE_URL or production Vercel URL)
    const baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://calendar-navy-eight.vercel.app';
    
    const res = await fetch(`${baseUrl}/api/admin/users`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error || 'Failed to fetch user directory');
    }

    const data = await res.json();
    users.value = data.users || data;
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};

// Search filter
const filteredUsers = computed(() => {
  if (!searchQuery.value) return users.value;
  const q = searchQuery.value.toLowerCase();
  return users.value.filter(u =>
    (u.name && u.name.toLowerCase().includes(q)) ||
    (u.email && u.email.toLowerCase().includes(q)) ||
    (u.user_id && u.user_id.toLowerCase().includes(q))
  );
});

// Date formatting helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

onMounted(() => {
  if (isAdmin.value) {
    fetchUsers();
  }
});
</script>

<template>
  <div class="grid-x full-width">
    <div class="cell small-12 medium-offset-1 medium-10 large-offset-1 large-10 white-section content-section">
      <div class="grid-x">
        <div class="cell small-12 medium-12 large-12">
          <div class="main-content">

            <div v-if="isAuthenticated">
              <h2 class="left-align">Admin user: {{ user?.meta_firstname || user?.name }} {{ user?.meta_lastname }}</h2>

              <!-- Display admin-specific UI -->
              <div v-if="isAdmin" class="admin-badge">
                <p>
                  <font-awesome-icon :icon="['fas', 'shield-halved']" /> Admin Dashboard Access 
                  <span class="tick"><font-awesome-icon :icon="['fas', 'circle-check']" /></span>
                </p>
              </div>
            </div>

            <h1 class="center-text">Admin Control Centre</h1>

            <!-- Access restriction callout -->
            <div v-if="!isAdmin" class="callout alert margin-top-1">
              <p>You do not have administrative permissions to view this control panel.</p>
            </div>

            <!-- Admin Content -->
            <div v-else class="admin-dashboard">
              
              <!-- Search & Refresh Toolbar -->
              <div class="dashboard-controls grid-x grid-margin-x align-middle">
                <div class="cell small-12 medium-6">
                  <input 
                    type="text" 
                    v-model="searchQuery" 
                    placeholder="Search users by name, email, or ID..." 
                    class="search-input"
                  />
                </div>
                <div class="cell small-12 medium-6 text-right">
                  <button @click="fetchUsers" class="button secondary small" :disabled="loading">
                    <font-awesome-icon :icon="['fas', 'rotate']" :spin="loading" /> Refresh List
                  </button>
                </div>
              </div>

              <!-- Loading / Error States -->
              <div v-if="loading" class="text-center padding-y">
                <p>Fetching user directory from Auth0...</p>
              </div>

              <div v-else-if="error" class="callout alert">
                <p>Unable to load users: {{ error }}</p>
              </div>

              <!-- Users Table -->
              <div v-else class="table-scroll">
                <table class="hover stack">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Logins</th>
                      <th>Last Login</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="u in filteredUsers" :key="u.user_id">
                      <td class="user-cell">
                        <img :src="u.picture" alt="Avatar" class="avatar" />
                        <div>
                          <strong>{{ u.name || u.nickname }}</strong>
                          <small class="user-id">{{ u.user_id }}</small>
                        </div>
                      </td>
                      <td>{{ u.email }}</td>
                      <td>
                        <span v-if="u.email_verified" class="badge success">Verified</span>
                        <span v-else class="badge warning">Unverified</span>
                      </td>
                      <td>{{ u.logins_count || 0 }}</td>
                      <td>{{ formatDate(u.last_login) }}</td>
                      <td>{{ formatDate(u.created_at) }}</td>
                    </tr>
                    <tr v-if="filteredUsers.length === 0">
                      <td colspan="6" class="text-center">No users match your search query.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use "@/assets/styles/abstract/variables" as *;

.tick {
  color: $green;
}

.admin-dashboard {
  margin-top: 2rem;

  .dashboard-controls {
    margin-bottom: 1.5rem;
    
    .search-input {
      margin-bottom: 0;
    }
  }

  .table-scroll {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;

    .user-cell {
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
      }

      .user-id {
        display: block;
        color: #777;
        font-size: 0.75rem;
      }
    }
  }

  .badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 4px;

    &.success {
      background-color: #d4edda;
      color: #155724;
    }

    &.warning {
      background-color: #fff3cd;
      color: #856404;
    }
  }

  .padding-y {
    padding: 2rem 0;
  }
}
</style>